import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Zap, Gift, Share2, Clock, QrCode, User, TrendingUp, TrendingDown, ShieldOff, Trash2, Megaphone, Send } from 'lucide-react';
import { adminApi } from '../../services/api';
import TierBadge, { TIER_AVATAR_BG } from '../../staff/components/TierBadge';
import { useBranding } from '../../context/BrandingContext';

import { getBackendUrl } from '../../services/origin';
const TX_LABELS = { purchase: 'Acquisto', manual_bonus: 'Bonus', redeem: 'Riscatto', referral: 'Referral', welcome: 'Benvenuto', birthday: 'Compleanno', expiry: 'Scadenza' };
const TX_COLORS = { purchase: 'text-[#3ECF8E]', manual_bonus: 'text-[#3ECF8E]', welcome: 'text-[#3ECF8E]', referral: 'text-[#3ECF8E]', birthday: 'text-[#3ECF8E]', redeem: 'text-[#EF4444]', expiry: 'text-[#EF4444]' };

// Compute progress toward next tier. `tierNames` comes from brand settings
// so we show "Oro", "Platino", etc. instead of the raw English default
// when the admin has relabelled tiers.
function getProgressToNextTier(lifetime, tierNames = {}) {
  const nm = { silver: 'Silver', gold: 'Gold', platinum: 'Platinum', ...tierNames };
  if (lifetime >= 1000) return { progress: 100, nextTier: null, pointsNeeded: 0 };
  if (lifetime >= 500) return { progress: ((lifetime - 500) / 500) * 100, nextTier: nm.platinum, pointsNeeded: 1000 - lifetime };
  if (lifetime >= 200) return { progress: ((lifetime - 200) / 300) * 100, nextTier: nm.gold, pointsNeeded: 500 - lifetime };
  return { progress: (lifetime / 200) * 100, nextTier: nm.silver, pointsNeeded: 200 - lifetime };
}

export default function AdminCustomerDetail() {
  const { tier_names } = useBranding();
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('transactions');
  const [campaignPrizes, setCampaignPrizes] = useState([]);
  const [activeCampaigns, setActiveCampaigns] = useState([]);
  const [showAssign, setShowAssign] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [assigning, setAssigning] = useState(false);

  const loadCampaignData = () => {
    adminApi.get(`/customers/${id}/campaign-prizes`)
      .then(r => setCampaignPrizes(r.data.prizes || []))
      .catch(() => {});
    adminApi.get('/campaigns/active')
      .then(r => setActiveCampaigns(r.data.campaigns || []))
      .catch(() => {});
  };

  useEffect(() => {
    adminApi.get(`/admin/customers/${id}`)
      .then(r => setData(r.data))
      .catch(() => { toast.error('Cliente non trovato'); navigate(-1); })
      .finally(() => setLoading(false));
    loadCampaignData();
     
  }, [id, navigate]);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-[#D44E20] border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!data) return null;

  const { customer: c, transactions, referrals_given, redemptions, stats } = data;
  const progress = getProgressToNextTier(c.lifetime_points || 0, tier_names);
  const isErased = c.status === 'erased';

  const handleResendCredentials = async () => {
    if (!window.confirm(`Inviare PIN e link bacheca via SMS al numero ${c.phone}?`)) return;
    try {
      const r = await adminApi.post(`/customers/${id}/resend-credentials`);
      if (r.data.sent) {
        const msg = r.data.rotated_pin
          ? 'PIN rigenerato e inviato via SMS!'
          : 'PIN e link bacheca inviati via SMS!';
        toast.success(msg);
      } else {
        toast.error('SMS non inviato: ' + (r.data.error || 'errore Twilio'));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore durante l\'invio');
    }
  };

  const handleCreditWelcomeBonus = async () => {
    if (!window.confirm(`Vuoi accreditare il bonus benvenuto a "${c.name}"?\nL'azione è idempotente: se è già stato accreditato non succede nulla.`)) return;
    try {
      const r = await adminApi.post(`/customers/${id}/credit-welcome-bonus`);
      if (r.data.credited) {
        toast.success(`+${r.data.points_credited} punti benvenuto accreditati!`);
        // Refresh
        const fresh = await adminApi.get(`/admin/customers/${id}`);
        setData(fresh.data);
      } else {
        toast.info(r.data.reason || 'Bonus già accreditato');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore durante l\'accredito');
    }
  };

  const handleGdprErase = async () => {
    const confirmation = window.prompt(
      `⚠️ ATTENZIONE — GDPR Right to Erasure\n\n` +
      `Stai per cancellare DEFINITIVAMENTE i dati personali di "${c.name}".\n` +
      `Questa azione:\n` +
      `• Anonimizza nome, telefono, email, data di nascita\n` +
      `• Disabilita la dashboard cliente (PIN e token rimossi)\n` +
      `• Mantiene transazioni e statistiche per integrità contabile\n` +
      `• È irreversibile\n\n` +
      `Per confermare, scrivi: ELIMINA`
    );
    if (confirmation !== 'ELIMINA') {
      toast.error('Cancellazione annullata');
      return;
    }
    try {
      await adminApi.delete(`/admin/customers/${id}/gdpr-erase`);
      toast.success('Dati personali del cliente anonimizzati (GDPR Art. 17)');
      navigate('/admin/customers');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore durante la cancellazione');
    }
  };

  const handleHardDelete = async () => {
    const confirmation = window.prompt(
      `⚠️ ELIMINAZIONE DEFINITIVA cliente\n\n` +
      `Stai per RIMUOVERE COMPLETAMENTE "${c.name}" e tutti i suoi dati:\n` +
      `• Anagrafica e contatti\n` +
      `• Tutte le transazioni e visite\n` +
      `• Premi accreditati e voucher in corso\n` +
      `• Riscatti coupon e referral collegati\n\n` +
      `Diversamente dall'erasure GDPR, NON resta neppure un record\n` +
      `anonimizzato. Le statistiche aggregate del business cambieranno.\n\n` +
      `Per confermare, scrivi: ELIMINA`
    );
    if (confirmation !== 'ELIMINA') {
      toast.error('Eliminazione annullata');
      return;
    }
    try {
      await adminApi.delete(`/customers/${id}`);
      toast.success(`Cliente ${c.name} eliminato definitivamente`);
      navigate('/admin/customers');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore durante l\'eliminazione');
    }
  };

  const tabs = [
    { id: 'transactions', label: 'Transazioni', icon: Clock, count: stats.total_transactions },
    { id: 'referrals', label: 'Referral', icon: Share2, count: stats.total_referrals },
    { id: 'redemptions', label: 'Riscatti', icon: Gift, count: redemptions.length },
    { id: 'campaigns', label: 'Campagne', icon: Megaphone, count: campaignPrizes.length },
  ];

  const handleAssignCampaign = async () => {
    if (!selectedCampaign) return;
    setAssigning(true);
    try {
      const res = await adminApi.post(`/campaigns/${selectedCampaign}/assign-prize`, { customer_id: id });
      if (res.data?.deferred) {
        toast.success('Campagna riservata. Il premio verrà assegnato dopo la firma del consenso privacy.');
      } else {
        toast.success(`Premio assegnato: ${res.data?.prize_name || 'campagna'}`);
      }
      setShowAssign(false);
      setSelectedCampaign('');
      loadCampaignData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nell\'assegnazione della campagna');
    } finally {
      setAssigning(false);
    }
  };

  return (
    <div className="p-6 md:p-8 lg:p-10 max-w-5xl" data-testid="admin-customer-detail">
      {/* Back button */}
      <button onClick={() => navigate(-1)} data-testid="back-to-customers" className="flex items-center gap-2 text-[#64748B] text-sm hover:text-[#1A1A1A] mb-6 transition-colors">
        <ArrowLeft size={16} /> Torna ai clienti
      </button>

      {/* Header card */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-6">
        <div className="flex items-start gap-4 mb-6">
          <div className={`w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0 ${TIER_AVATAR_BG[c.tier] || 'bg-gray-200'}`}>
            <span className="text-white font-bold text-2xl">{c.name?.charAt(0)}</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl md:text-3xl tracking-tight font-bold text-[#1A1A1A]">{c.name}</h1>
              <TierBadge tier={c.tier} />
            </div>
            <div className="flex flex-wrap gap-4 mt-1 text-sm text-[#64748B]">
              <span>{c.phone}</span>
              {c.email && <span>{c.email}</span>}
              <span>Iscritto il {c.joined_at ? new Date(c.joined_at).toLocaleDateString('it-IT') : '—'}</span>
              {c.registered_by_staff_name && (
                <span data-testid="registered-by-chip" className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#9333EA]/10 text-[#9333EA] text-xs font-medium">
                  Registrato da: {c.registered_by_staff_name}
                </span>
              )}
            </div>
          </div>
          <div className="flex-shrink-0 flex items-center gap-2 flex-wrap">
            {/* Re-send PIN + dashboard link via SMS — useful when a
                customer forgets their PIN. Always available so the
                staff can resolve the issue at the till in one click. */}
            <button
              onClick={handleResendCredentials}
              data-testid="resend-credentials-btn"
              className="flex items-center gap-2 px-4 py-2 bg-[#D44E20]/10 border border-[#D44E20]/30 rounded-xl text-sm text-[#D44E20] hover:bg-[#D44E20]/20 transition-colors"
              title="Invia PIN e link bacheca via SMS"
            >
              <Send size={16} /> Reinvia PIN
            </button>
            {/* Show "Accredita bonus benvenuto" only when the customer
                appears stuck (0 lifetime points and no welcome tx).
                The endpoint is itself idempotent, so this is just to
                avoid clutter for normal customers. */}
            {(c.lifetime_points === 0 || !c.lifetime_points) && (
              <button
                onClick={handleCreditWelcomeBonus}
                data-testid="credit-welcome-bonus-btn"
                className="flex items-center gap-2 px-4 py-2 bg-[#3ECF8E]/10 border border-[#3ECF8E]/30 rounded-xl text-sm text-[#3ECF8E] hover:bg-[#3ECF8E]/20 transition-colors"
                title="Accredita il bonus benvenuto al cliente"
              >
                <Zap size={16} /> Bonus benvenuto
              </button>
            )}
            <a
              href={`${getBackendUrl()}/api/customers/${c.id}/qrcode`}
              target="_blank" rel="noreferrer"
              data-testid="view-qr-btn"
              className="flex items-center gap-2 px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm text-[#64748B] hover:bg-slate-100 transition-colors"
            >
              <QrCode size={16} /> QR Code
            </a>
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <StatCard icon={Zap} label="Punti disponibili" value={c.points?.toLocaleString('it-IT')} color="#D44E20" />
          <StatCard icon={TrendingUp} label="Punti guadagnati" value={stats.points_earned?.toLocaleString('it-IT')} color="#3ECF8E" />
          <StatCard icon={TrendingDown} label="Punti spesi" value={stats.points_spent?.toLocaleString('it-IT')} color="#EF4444" />
          <StatCard icon={User} label="Visite totali" value={c.total_visits} color="#3B82F6" />
        </div>

        {/* Progress bar */}
        {progress.nextTier && (
          <div className="mt-5">
            <div className="flex justify-between text-xs text-[#64748B] mb-1.5">
              <span>{Math.round(progress.progress)}% verso {progress.nextTier}</span>
              <span className="tabular-nums">{progress.pointsNeeded} pt mancanti</span>
            </div>
            <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-[#D44E20] rounded-full transition-all" style={{ width: `${Math.min(progress.progress, 100)}%` }} />
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`detail-tab-${tab.id}`}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-[#D44E20] text-white'
                : 'bg-white text-[#64748B] border border-slate-200 hover:bg-slate-50'
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
            <span className={`text-xs px-1.5 py-0.5 rounded-full ${activeTab === tab.id ? 'bg-white/20' : 'bg-slate-100'}`}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        {activeTab === 'transactions' && (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-slate-100">
              <tr>
                {['Data', 'Tipo', 'Dettaglio', 'Operatore', 'Punti'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {transactions.map(tx => (
                <tr key={tx.id} data-testid={`tx-row-${tx.id}`} className="hover:bg-slate-50/50">
                  <td className="px-4 py-3 text-[#64748B] text-xs whitespace-nowrap">
                    {tx.created_at ? new Date(tx.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                      tx.type === 'redeem' || tx.type === 'expiry' ? 'bg-red-50 text-[#EF4444]' :
                      tx.type === 'purchase' ? 'bg-green-50 text-[#3ECF8E]' :
                      'bg-slate-100 text-[#64748B]'
                    }`}>
                      {TX_LABELS[tx.type] || tx.type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[#1A1A1A] text-sm">{tx.note || '—'}</td>
                  <td className="px-4 py-3 text-[#64748B] text-sm">{tx.staff_name}</td>
                  <td className={`px-4 py-3 font-bold text-sm tabular-nums ${TX_COLORS[tx.type] || 'text-[#1A1A1A]'}`}>
                    {tx.points_delta > 0 ? '+' : ''}{tx.points_delta}
                  </td>
                </tr>
              ))}
              {transactions.length === 0 && (
                <tr><td colSpan={5} className="text-center py-12 text-[#64748B]">Nessuna transazione</td></tr>
              )}
            </tbody>
          </table>
        )}

        {activeTab === 'referrals' && (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-slate-100">
              <tr>
                {['Data', 'Telefono invitato', 'Stato'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {referrals_given.map(r => (
                <tr key={r.id} data-testid={`ref-row-${r.id}`}>
                  <td className="px-4 py-3 text-[#64748B] text-xs">
                    {r.created_at ? new Date(r.created_at).toLocaleDateString('it-IT') : '—'}
                  </td>
                  <td className="px-4 py-3 text-[#1A1A1A] text-sm">{r.referred_phone}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${r.status === 'completed' ? 'bg-green-50 text-[#3ECF8E]' : 'bg-slate-100 text-[#64748B]'}`}>
                      {r.status === 'completed' ? 'Completato' : 'In attesa'}
                    </span>
                  </td>
                </tr>
              ))}
              {referrals_given.length === 0 && (
                <tr><td colSpan={3} className="text-center py-12 text-[#64748B]">Nessun referral</td></tr>
              )}
            </tbody>
          </table>
        )}

        {activeTab === 'redemptions' && (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-slate-100">
              <tr>
                {['Data', 'Premio', 'Punti spesi'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {redemptions.map(r => (
                <tr key={r.id} data-testid={`red-row-${r.id}`}>
                  <td className="px-4 py-3 text-[#64748B] text-xs">
                    {r.created_at ? new Date(r.created_at).toLocaleDateString('it-IT') : '—'}
                  </td>
                  <td className="px-4 py-3 text-[#1A1A1A] text-sm">
                    {r.reward_emoji} {r.reward_name}
                  </td>
                  <td className="px-4 py-3 text-[#EF4444] font-bold text-sm tabular-nums">-{r.points_spent}</td>
                </tr>
              ))}
              {redemptions.length === 0 && (
                <tr><td colSpan={3} className="text-center py-12 text-[#64748B]">Nessun riscatto</td></tr>
              )}
            </tbody>
          </table>
        )}

        {activeTab === 'campaigns' && (
          <div className="p-4" data-testid="tab-campaigns">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-[#64748B]">
                {campaignPrizes.length === 0 ? 'Nessun premio campagna assegnato.' : `${campaignPrizes.length} premio${campaignPrizes.length > 1 ? 'i' : ''} campagna`}
              </p>
              <button
                onClick={() => setShowAssign(true)}
                disabled={!activeCampaigns.length}
                data-testid="admin-open-assign-campaign-btn"
                className="px-4 py-2 bg-[#D44E20] text-white rounded-xl text-sm font-semibold disabled:opacity-40 hover:bg-[#b8411a] flex items-center gap-2"
              >
                <Megaphone size={14} /> Assegna campagna
              </button>
            </div>
            {campaignPrizes.length === 0 ? (
              <div className="text-center py-10 text-[#64748B] text-sm">
                {activeCampaigns.length
                  ? 'Usa "Assegna campagna" per riservare un premio a questo cliente.'
                  : 'Non ci sono campagne attive al momento.'}
              </div>
            ) : (
              <div className="space-y-2">
                {campaignPrizes.map(p => (
                  <div key={p.id} data-testid={`admin-campaign-prize-${p.id}`} className="border border-slate-200 rounded-xl p-3 flex items-center gap-3">
                    <Megaphone size={18} className="text-[#D44E20]" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[#1A1A1A] font-semibold text-sm">{p.prize_name}</p>
                      <p className="text-[#64748B] text-xs">{p.campaign_name} · {p.coupon_type}</p>
                    </div>
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                      p.status === 'available' ? 'bg-green-50 text-[#3ECF8E]' :
                      p.status === 'redeemed' ? 'bg-slate-100 text-[#64748B]' :
                      'bg-red-50 text-[#EF4444]'
                    }`}>
                      {p.status === 'available' ? 'Disponibile' : p.status === 'redeemed' ? 'Riscattato' : 'Scaduto'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {!isErased && (
        <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4 mt-6" data-testid="gdpr-zone">
          <div className="flex items-start gap-3">
            <ShieldOff size={18} className="text-rose-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-sm text-rose-900">Zona privacy / GDPR</h3>
              <p className="text-xs text-rose-700 mt-1">
                Diritto all'oblio (GDPR Art. 17): cancella i dati personali del cliente mantenendo le sole statistiche aggregate per finalità contabili. Azione irreversibile.
              </p>
              <button
                onClick={handleGdprErase}
                data-testid="gdpr-erase-btn"
                className="mt-3 inline-flex items-center gap-2 px-3 py-2 bg-white border border-rose-300 text-rose-700 hover:bg-rose-100 rounded-xl text-xs font-semibold transition-colors"
              >
                <ShieldOff size={13} />
                Cancella dati personali (GDPR)
              </button>
              <button
                onClick={handleHardDelete}
                data-testid="hard-delete-customer-btn"
                className="mt-3 ml-2 inline-flex items-center gap-2 px-3 py-2 bg-rose-600 border border-rose-700 text-white hover:bg-rose-700 rounded-xl text-xs font-semibold transition-colors"
              >
                <Trash2 size={13} />
                Elimina cliente (hard delete)
              </button>
            </div>
          </div>
        </div>
      )}
      {isErased && (
        <div className="bg-slate-100 border border-slate-200 rounded-2xl p-4 mt-6 text-center text-xs text-slate-500" data-testid="gdpr-erased-banner">
          Dati personali di questo cliente cancellati su richiesta GDPR.
        </div>
      )}

      {showAssign && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => !assigning && setShowAssign(false)} data-testid="admin-assign-campaign-modal">
          <div className="bg-white rounded-2xl p-5 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-[#1A1A1A] mb-1">Assegna campagna</h3>
            <p className="text-xs text-[#64748B] mb-4">Verrà estratto a sorte un premio dalla campagna selezionata.</p>
            <label className="text-xs text-[#64748B] block mb-2">Campagna attiva</label>
            <select
              value={selectedCampaign}
              onChange={e => setSelectedCampaign(e.target.value)}
              data-testid="admin-assign-campaign-select"
              className="w-full h-11 rounded-xl border border-slate-200 px-3 text-sm focus:border-[#D44E20] focus:outline-none mb-4"
            >
              <option value="">Seleziona…</option>
              {activeCampaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <div className="flex gap-2">
              <button onClick={() => setShowAssign(false)} disabled={assigning} className="flex-1 h-11 rounded-xl border border-slate-200 text-sm font-medium hover:bg-slate-50">Annulla</button>
              <button
                onClick={handleAssignCampaign}
                disabled={!selectedCampaign || assigning}
                data-testid="admin-confirm-assign-campaign-btn"
                className="flex-1 h-11 rounded-xl bg-[#D44E20] text-white text-sm font-bold disabled:opacity-50 hover:bg-[#b8411a]"
              >
                {assigning ? 'Assegnazione…' : 'Assegna'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-gray-50 rounded-xl p-4 border border-slate-100">
      <div className="flex items-center gap-2 mb-1">
        <Icon size={14} style={{ color }} />
        <span className="text-[#64748B] text-xs">{label}</span>
      </div>
      <div className="font-bold text-xl tabular-nums" style={{ color }}>{value}</div>
    </div>
  );
}
